import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, isEqual, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { AlertRatio } from '@/components/AlertRatio'
import { Button, Checkbox, DateTimePicker, Input, Modal } from '@/components/ui'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import { cn } from '@/lib/utils'
import { getTranslation } from '../../account/services/auth'
import {
  Artist,
  ArtistDetails,
  ArtistPayload,
  Languages,
} from '../../common/models'
import FormGroup from '../../core/components/FormGroup'
import { FormUploadPreview } from '../../core/components/FormUploadPreview'
import { formatDateTime } from '../../core/constants/times'
import {
  createArtist,
  deleteArtist,
  getAllArtists,
  updateArtist,
} from '../../core/services/artist'
import translate from '../../core/services/translate'
import { uploadImage } from '../../core/services/upload-image'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { MessageBox } from './messages/MessageBox'
import { Icon } from '@/common/components/Icon'

export interface CreateArtistModalProps {
  showEditModal: boolean
  artist?: ArtistDetails
  setShowEditModal: (show: boolean) => void
  setArtists: (data: Artist[]) => void
  setTotalElemenets: (data: number) => void
  setPage: (data: number) => void
  editMode: boolean
  size: number
  page: number
  allLanguages: Languages[]
}

interface IArtistContent {
  locale: string
  description: string
}

interface IArtistValues {
  name: string
  content?: IArtistContent[]
  weight: number
  published: boolean
  defaultImage?: boolean
  photo?: string
  publishedDate?: string
  webLink?: string
  fbLink?: string
  twLink?: string
  igLink?: string
  youtubeLink?: string
  tiktokLink?: string
}

export const CreateArtistModal = (
  props: CreateArtistModalProps,
): JSX.Element => {
  const {
    showEditModal,
    setShowEditModal,
    artist,
    editMode,
    setArtists,
    page,
    setPage,
    size,
    setTotalElemenets,
    allLanguages,
  } = props
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [content, setContent] = useState<IArtistContent[]>([])

  const language = getTranslation()

  useEffect(() => {
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        description: '',
      }
    })
    setValue('content', temp)
    if (artist) {
      if (!!artist.translations && artist.translations.length > 0) {
        setContent(artist.translations)
        setTimeout(() => {
          map(artist.translations, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              setValue(`content.${index}.description`, trans.description)
              setValue(`content.${index}.locale`, trans.locale)
            }
          })
        }, 100)
      }
      setValue('name', artist.name, { shouldValidate: true })
      setValue('publishedDate', formatDateTime(artist.publishedAt.valueOf()))
      setValue('published', artist.published, { shouldValidate: true })
      setValue('weight', artist.weight, { shouldValidate: true })
      setValue('webLink', artist.webLink)
      setValue('fbLink', artist.fbLink)
      setValue('twLink', artist.twLink)
      setValue('youtubeLink', artist.youtubeLink)
      setValue('igLink', artist.igLink)
      setValue('tiktokLink', artist.tiktokLink)
      setValue('defaultImage', artist.defaultImage)
      setValue('photo', artist.imageLink)
      setImage(artist.imageLink)
      if (artist?.imageLink) {
        const url = new URL(artist?.imageLink)
        const i = url?.pathname?.lastIndexOf('/') + 1
        const imageName = url?.pathname?.substring(i)
        setImageName(imageName)
      }
    }
    return () => {
      setImage('')
      setImageName('')
      reset()
    }
  }, [artist, showEditModal])

  const handleClose = () => {
    setShowEditModal(false)
    setImage('')
    setImageName('')
  }

  const schema = yup.object().shape({
    content: yup
      .array()
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          description: yup.string().required('Field is required'),
        }),
      )
      .required(),
    name: yup.string().required('Field is required'),
    weight: yup.number().required('Field is required'),
    published: yup.boolean().required('Field is required'),
    defaultImage: yup.boolean(),
    photo: yup.string().when('defaultImage', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) => schema.required(),
    }),
    webLink: yup.string(),
    twLink: yup.string(),
    fbLink: yup.string(),
    youtubeLink: yup.string(),
    tiktokLink: yup.string(),
    igLink: yup.string(),
    publishedDate: yup.string(),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<IArtistValues>({
    mode: 'onChange',
    resolver: yupResolver<IArtistValues>(schema),
    defaultValues: {
      name: '',
      weight: undefined,
      publishedDate: formatDateTime(new Date().valueOf()),
      published: false,
      webLink: '',
      fbLink: '',
      twLink: '',
      igLink: '',
      youtubeLink: '',
      tiktokLink: '',
      photo: '',
      content: [],
    },
  })

  const contentWatch = watch('content')
  const dafaultImageWatch = watch('defaultImage')

  const submit = async (values: IArtistValues) => {
    const data: ArtistPayload = {
      name: values.name,
      translations: values.content,
      weight: values.weight,
      publishedAt: values.publishedDate
        ? new Date(values.publishedDate).valueOf()
        : new Date().valueOf(),
      published: values.published,
      webLink: values.webLink,
      fbLink: values.fbLink,
      twLink: values.twLink,
      igLink: values.igLink,
      youtubeLink: values.youtubeLink,
      tiktokLink: values.tiktokLink,
      defaultImage: values.defaultImage,
    }
    if (values.photo) {
      data.imageLink = values.photo
      data.thumbnailLink = values.photo
    }
    if (artist?.id) {
      try {
        const { status } = await updateArtist(artist.id, data)
        const response = await getAllArtists({ page: 0, size })
        setPage(1)
        setArtists(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    } else {
      try {
        const { status } = await createArtist(data)
        const response = await getAllArtists({ page, size })
        setArtists(response.data.content)
        setTotalElemenets(response.data.totalElements)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    }
    handleClose()
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const deleteArtistFromModal = () => {
    // prettier-ignore
    (async () => {
      try {
        if (artist) {
          const { status } = await deleteArtist([artist.id])

          const response = await getAllArtists({ page, size })
          setArtists(response.data.content)
          setTotalElemenets(response.data.totalElements)
          if (response.data.totalPages <= page) {
            setPage(response.data.pageNumber)
          } else {
            setPage(response.data.pageNumber + 1)
          }
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5 text-2xl font-bold leading-none text-monochrome-1">
      {children}
    </div>
  )

  return (
    <>
      <Modal
        open={showEditModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Artists"
              subtitle={artist ? artist.name : 'Add New Artist'}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {editMode ? (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  isSubmitting ||
                  isUploading ||
                  (Object.keys(dirtyFields).length === 0 &&
                    isEqual(contentWatch, content)) ||
                  (isErrorRatio && !dafaultImageWatch)
                }
                form="artist-form"
                type="submit"
                variant="primary"
                loading={isSubmitting || isUploading}
              >
                {artist?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <SectionTitle>Main Info</SectionTitle>
        <form
          id="artist-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          {map(allLanguages, (language, index) => {
            return (
              <div
                className={cn(
                  'flex w-full flex-col gap-3 rounded-[30px] border border-monochrome-120 bg-monochrome-120 p-5',
                )}
                key={language.id}
              >
                <FormGroup label="Language" wrapperClassName="mb-0">
                  <Input
                    value={language.name}
                    readOnly
                    className="bg-monochrome-120"
                  />
                </FormGroup>
                <HtmlEditor
                  index={index}
                  label="Description (Optional)"
                  name={`content.${index}.description`}
                  variables={[]}
                  control={control}
                  value={contentWatch?.[index]?.description || ''}
                  onChange={(data) => {
                    setValue(`content.${index}.description`, data)
                  }}
                  isError={!!errors?.content?.[index]?.description?.message}
                />
              </div>
            )
          })}

          <div className="flex w-full flex-col">
            <FormGroup
              label="Weight"
              tooltip={translate(language.value, 'artists.weightTooltip')}
            >
              <Input
                isInvalid={Boolean(errors?.weight?.message)}
                {...register('weight')}
                type="text"
                placeholder="Enter Weight"
              />
            </FormGroup>
          </div>
          <FormGroup label="Publish Date (Optional)">
            <DateTimePicker
              hasError={!!errors?.publishedDate?.message}
              {...register('publishedDate')}
              onChange={(date) => {
                register('publishedDate').onChange({
                  target: {
                    value: date ? formatDateTime(date.valueOf()) : '',
                  },
                })
                setValue(
                  'publishedDate',
                  date ? formatDateTime(date.valueOf()) : '',
                  { shouldValidate: true },
                )
              }}
              placeholder="Enter Publish Date"
            />
          </FormGroup>
          <FormGroup
            label="Published"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
          <div className="my-4 h-px w-full bg-monochrome-120" />

          <SectionTitle>Social Media</SectionTitle>

          <FormGroup label="Website (Optional)">
            <Input
              isInvalid={Boolean(errors?.webLink?.message)}
              {...register('webLink')}
              type="text"
              placeholder="Enter Website URL"
            />
          </FormGroup>
          <FormGroup label="Facebook URL (Optional)">
            <Input
              isInvalid={Boolean(errors?.fbLink?.message)}
              {...register('fbLink')}
              type="text"
              placeholder="Enter Facebook URL"
            />
          </FormGroup>
          <FormGroup label="Twitter URL (Optional)">
            <Input
              isInvalid={Boolean(errors?.twLink?.message)}
              {...register('twLink')}
              type="text"
              placeholder="Enter Twitter URL"
            />
          </FormGroup>
          <FormGroup label="Youtube URL (Optional)">
            <Input
              isInvalid={Boolean(errors?.youtubeLink?.message)}
              {...register('youtubeLink')}
              type="text"
              placeholder="Enter Youtube URL"
            />
          </FormGroup>
          <FormGroup label="Instagram URL (Optional)">
            <Input
              isInvalid={Boolean(errors?.igLink?.message)}
              {...register('igLink')}
              type="text"
              placeholder="Enter Instagram URL"
            />
          </FormGroup>
          <FormGroup label="Tik Tok URL (Optional)">
            <Input
              isInvalid={Boolean(errors?.tiktokLink?.message)}
              {...register('tiktokLink')}
              type="text"
              placeholder="Enter Tic Tok URL"
            />
          </FormGroup>
          <div className="my-4 h-px w-full bg-monochrome-120" />
          <SectionTitle>Photo</SectionTitle>

          <FormGroup
            label="Default Image"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
          >
            <Controller
              name="defaultImage"
              control={control}
              render={({ field }) => (
                <Checkbox
                  className="border-monochrome-1"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
          <FormUploadPreview
            disabled={dafaultImageWatch}
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={Boolean(
                  errors?.photo?.message || invalidPhoto || isErrorRatio,
                )}
                disabled={dafaultImageWatch}
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'ARTIST',
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue('photo', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue('photo', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Photo (335X335)"
            previewLabel="Photo (335X335)"
          />
          {isErrorRatio && <AlertRatio />}
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle={'Artist'}
        deleteFunc={deleteArtistFromModal}
        deletedItem={artist?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
